// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// apps/admin/src/pages/billing/PayoutsQueuePage.tsx
//
// Phase 8.2 Wave 2 Task 09 — Payouts queue (vendor payout state machine).
//
// Per CONTEXT.md +.md §6.26 + API-CONTRACTS.md §/admin/payouts:
//   - Payouts page (vendor payout state machine + KYC gate).
//   - State machine Pending → Processing → Completed/Failed.
//                 transferredBy/At/transferReference/transferProofUrl on Completed.
//   - SINGLE approve per row (NO bulk action checkbox column).
//   - Failed status = NO auto-retry; earnings rollback to Available
//                 wallet shown to admin so the policy is unambiguous.
//   - Pre-KYC vendor payouts blocked (UI surface: filter chip +
//                 inline badge; gate enforced server-side via 422 KYC_BLOCKED).
//
// Endpoints (per API-CONTRACTS.md):
//   GET    /api/v1/admin/payouts?page=&limit=&status=&vendor_id=&date_from=&date_to=&sort=
//   POST   /api/v1/admin/payouts/:id/approve
//   POST   /api/v1/admin/payouts/:id/reject
//   POST   /api/v1/admin/payouts/:id/mark-transferred  (modal — see MarkTransferredModal.tsx)
//
// UI surfaces:
//   1. Summary cards: Total Pending USD + Approved This Month USD.
//   2. Bar chart of payout volume per status across visible rows (a lightweight
//      SVG bars — the deeper monthly trend chart is deferred to a Reports phase).
//   3. Tabs: All / Pending / Processing / Completed / Failed.
//   4. Pre-KYC filter chip — orthogonal to status; surfaces vendor.kycStatus = pending.
//   5. Single Approve / Reject / Mark Transferred buttons per row — NO checkbox column.
//   6. Failed tab: shows fail_reason + "no-auto-retry" earnings-rollback notice.
//
// Test IDs (from PLAN/task-09.md <test_ids>):
//   - admin-payouts-page                   — root <main>
//   - payouts-summary-pending              — pending summary card
//   - payouts-summary-approved             — approved-this-month card
//   - payouts-summary-chart                — SVG bar chart container
//   - payouts-tab-{all|pending|processing|completed|failed}
//   - payouts-filter-prekyc-chip           — Pre-KYC pending filter chip
//   - payout-row-{payoutId}                — list row
//   - payout-approve-btn-{payoutId}
//   - payout-reject-btn-{payoutId}
//   - payout-mark-transferred-btn-{payoutId}
//   - payout-failed-no-retry-notice-{payoutId}
//
// vg-binding: PLAN:task-09
// vg-binding: P8.D-86
// vg-binding: P8.D-190
// vg-binding: P8.D-451
// vg-binding: P8.D-452
// vg-binding: BR-1
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:idempotency-key
//
// Phase 9.2 Wave 1 Task 03 — i18n hardening (49 VN literals → t() calls under
// `admin.payouts.queue.*` + `admin.payouts.approve.*` + `admin.payouts.reject.*`).
// vg-binding: PLAN:task-03
// vg-binding: P9.2.D-01
// vg-binding: P9.2.D-02
// vg-binding: P9.2.D-04
// vg-binding: P9.2.D-11
// vg-binding: P9.2.D-12

import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
  useToast,
  PageHeader,
} from '@printway/ui';
import { PageListFooter } from '../../components/datatable/PageListFooter.js';

import { ApiError, apiClient } from '../../lib/apiClient.js';
import { Money } from '../../components/ui/Money.js';
import { BillingStatusBadge } from '../../components/billing/BillingStatusBadge.js';
import { listPayouts } from '../../api/finance.api.js';
import { financeKeys } from '../../api/queries/financeKeys.js';
import type {
  PayoutListFilters,
  PayoutRow,
  PayoutStatus,
} from '../../types/finance.js';
import {
  MarkTransferredModal,
  type MarkTransferredPayoutSummary,
} from '../../features/billing/MarkTransferredModal.js';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Tab metadata — orthogonal to the Pre-KYC filter chip.
// ---------------------------------------------------------------------------

type TabKey = 'all' | 'pending' | 'processing' | 'completed' | 'failed';

interface TabDescriptor {
  key: TabKey;
  labelKey: string;
  /** Mapped to the API `status` query param (or undefined for "all"). */
  apiStatus?: PayoutListFilters['status'];
  activeClass: string;
  idleClass: string;
}

const TABS: ReadonlyArray<TabDescriptor> = [
  {
    key: 'all',
    labelKey: 'payouts.queue.tab.all',
    apiStatus: 'all',
    activeClass: 'bg-pw-text text-white border-pw-text',
    idleClass:
      'border-pw-border bg-pw-bg text-pw-text-muted hover:bg-pw-surface',
  },
  {
    key: 'pending',
    labelKey: 'payouts.queue.tab.pending',
    apiStatus: 'Pending',
    activeClass: 'bg-pw-primary text-white border-pw-primary',
    idleClass:
      'border-pw-border bg-pw-bg text-pw-text-muted hover:bg-pw-surface',
  },
  {
    key: 'processing',
    labelKey: 'payouts.queue.tab.processing',
    apiStatus: 'Processing',
    activeClass: 'border-amber-500 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40',
    idleClass:
      'border-pw-border bg-pw-bg text-pw-text-muted hover:bg-pw-surface',
  },
  {
    key: 'completed',
    labelKey: 'payouts.queue.tab.completed',
    apiStatus: 'Completed',
    activeClass: 'border-emerald-500 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40',
    idleClass:
      'border-pw-border bg-pw-bg text-pw-text-muted hover:bg-pw-surface',
  },
  {
    key: 'failed',
    labelKey: 'payouts.queue.tab.failed',
    apiStatus: 'Failed',
    activeClass: 'border-rose-500 text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40',
    idleClass:
      'border-pw-border bg-pw-bg text-pw-text-muted hover:bg-pw-surface',
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function newIdempotencyKey(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `payout-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function useFormatRelativeTime(): (iso: string) => string {
  const { t } = useTranslation('admin');
  return (iso: string): string => {
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) return '—';
    const diffMs = Date.now() - ts;
    if (diffMs < 0) return new Date(iso).toLocaleString();
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return t('payouts.queue.relative.justNow');
    if (mins < 60) return t('payouts.queue.relative.minutes', { count: mins });
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return t('payouts.queue.relative.hours', { count: hrs });
    const days = Math.floor(hrs / 24);
    return t('payouts.queue.relative.days', { count: days });
  };
}

// Phase 8.x Debug B3 — `statusBadgeClasses` replaced by `<BillingStatusBadge>`
// canonical pill component (apps/admin/src/components/billing/BillingStatusBadge.tsx).

// ---------------------------------------------------------------------------
// Approve confirm dialog —: SINGLE approve (no bulk).
// ---------------------------------------------------------------------------

function ApproveConfirmDialog({
  payout,
  open,
  onOpenChange,
  onConfirmed,
}: {
  payout: PayoutRow | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onConfirmed: () => void;
}): JSX.Element | null {
  const { toast } = useToast();
  const { t } = useTranslation('admin');
  const [submitting, setSubmitting] = useState(false);
  // G12: one key PER ACTION-CLICK, held in state — a retry after a network
  // error re-sends the SAME key so the server dedupes instead of double-
  // approving. Regenerated each time the dialog opens for a (new) target.
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() =>
    newIdempotencyKey(),
  );
  useEffect(() => {
    if (open) setIdempotencyKey(newIdempotencyKey());
  }, [open]);

  if (!payout) return null;

  async function handleApprove(): Promise<void> {
    if (!payout) return;
    setSubmitting(true);
    try {
      await apiClient.post(
        `/api/v1/admin/payouts/${encodeURIComponent(payout.id)}/approve`,
        {},
        { idempotencyKey },
      );
      toast({
        variant: 'success',
        title: t('payouts.approve.toast.successTitle'),
        description: t('payouts.approve.toast.successDescription', {
          vendor: payout.vendor_name,
        }),
      });
      onOpenChange(false);
      onConfirmed();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'SELF_APPROVAL_FORBIDDEN') {
          // PR-3 maker-checker: the admin who created this withdrawal on the
          // vendor's behalf cannot approve it — another admin must.
          toast({
            variant: 'error',
            title: t('payouts.approve.toast.selfApprovalTitle'),
            description: t('payouts.approve.toast.selfApprovalDescription'),
          });
          return;
        }
        if (err.status === 422 && err.code === 'KYC_BLOCKED') {
          toast({
            variant: 'error',
            title: t('payouts.approve.toast.kycBlockedTitle'),
            description:
              err.message ||
              t('payouts.approve.toast.kycBlockedFallback'),
          });
          return;
        }
        if (err.status === 409) {
          toast({
            variant: 'warning',
            title: t('payouts.approve.toast.stateChangedTitle'),
            description:
              err.message ||
              t('payouts.approve.toast.stateChangedFallback'),
          });
          onOpenChange(false);
          onConfirmed();
          return;
        }
        toast({
          variant: 'error',
          title: t('payouts.approve.toast.approveFailedTitle'),
          description: err.message,
        });
        return;
      }
      toast({
        variant: 'error',
        title: t('payouts.approve.toast.networkErrorTitle'),
        description:
          err instanceof Error
            ? err.message
            : t('payouts.approve.toast.networkErrorFallback'),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return;
        onOpenChange(next);
      }}
      aria-label={t('payouts.approve.ariaLabel', { vendor: payout.vendor_name })}
    >
      <DialogHeader>
        <DialogTitle>
          {t('payouts.approve.title', { vendor: payout.vendor_name })}
        </DialogTitle>
        <p className="mt-1 text-sm text-pw-text-muted">
          {t('payouts.approve.bodyLead')}{' '}
          <strong>{t('payouts.approve.bodyFrom')}</strong>{' '}
          {t('payouts.approve.bodyTo')}{' '}
          <strong>{t('payouts.approve.bodyDest')}</strong>
          {t('payouts.approve.bodyTail')}
        </p>
      </DialogHeader>
      <div className="mt-4 rounded-md border border-pw-border bg-pw-surface p-3 text-sm">
        <div className="flex justify-between">
          <span className="text-pw-text-muted">
            {t('payouts.approve.amountLabel')}
          </span>
          <Money usdCents={payout.net_cents} align="right" />
        </div>
        <div className="mt-1 flex justify-between">
          <span className="text-pw-text-muted">
            {t('payouts.approve.gatewayLabel')}
          </span>
          <span className="text-pw-text">{payout.gateway_code}</span>
        </div>
      </div>
      <DialogFooter>
        <Button
          type="button"
          variant="secondary"
          onClick={() => onOpenChange(false)}
          disabled={submitting}
        >
          {t('payouts.approve.cancel')}
        </Button>
        <Button
          type="button"
          data-testid={`payout-approve-confirm-btn-${payout.id}`}
          onClick={() => void handleApprove()}
          disabled={submitting}
        >
          {submitting
            ? t('payouts.approve.submitting')
            : t('payouts.approve.confirm')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Reject confirm dialog — reason required (10-500 chars per contract).
// ---------------------------------------------------------------------------

function RejectConfirmDialog({
  payout,
  open,
  onOpenChange,
  onConfirmed,
}: {
  payout: PayoutRow | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onConfirmed: () => void;
}): JSX.Element | null {
  const { toast } = useToast();
  const { t } = useTranslation('admin');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // G12: stable key per action-click (see ApproveConfirmDialog).
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() =>
    newIdempotencyKey(),
  );
  useEffect(() => {
    if (open) setIdempotencyKey(newIdempotencyKey());
  }, [open]);

  if (!payout) return null;

  function reset(): void {
    setReason('');
    setReasonError(null);
    setSubmitting(false);
  }

  async function handleReject(): Promise<void> {
    if (!payout) return;
    const trimmed = reason.trim();
    if (trimmed.length < 10) {
      setReasonError(t('payouts.reject.errorMin'));
      return;
    }
    if (trimmed.length > 500) {
      setReasonError(t('payouts.reject.errorMax'));
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post(
        `/api/v1/admin/payouts/${encodeURIComponent(payout.id)}/reject`,
        { reason: trimmed },
        { idempotencyKey },
      );
      toast({
        variant: 'success',
        title: t('payouts.reject.toast.successTitle'),
        description: t('payouts.reject.toast.successDescription', {
          vendor: payout.vendor_name,
        }),
      });
      reset();
      onOpenChange(false);
      onConfirmed();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          toast({
            variant: 'warning',
            title: t('payouts.reject.toast.stateChangedTitle'),
            description:
              err.message ||
              t('payouts.reject.toast.stateChangedFallback'),
          });
          reset();
          onOpenChange(false);
          onConfirmed();
          return;
        }
        toast({
          variant: 'error',
          title: t('payouts.reject.toast.rejectFailedTitle'),
          description: err.message,
        });
        return;
      }
      toast({
        variant: 'error',
        title: t('payouts.reject.toast.networkErrorTitle'),
        description:
          err instanceof Error
            ? err.message
            : t('payouts.reject.toast.networkErrorFallback'),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return;
        if (!next) reset();
        onOpenChange(next);
      }}
      aria-label={t('payouts.reject.ariaLabel', { vendor: payout.vendor_name })}
    >
      <DialogHeader>
        <DialogTitle>
          {t('payouts.reject.title', { vendor: payout.vendor_name })}
        </DialogTitle>
        <p className="mt-1 text-sm text-pw-text-muted">
          {t('payouts.reject.bodyLead')}{' '}
          <strong>{t('payouts.reject.bodyBold')}</strong>{' '}
          {t('payouts.reject.bodyTail')}
        </p>
      </DialogHeader>
      <div className="mt-4 space-y-4">
        <div>
          <label
            htmlFor="payout-reject-reason"
            className="mb-1 block text-sm font-medium text-pw-text"
          >
            {t('payouts.reject.reasonLabel')}{' '}
            <span className="text-rose-600 dark:text-rose-300">
              {t('payouts.reject.reasonRequired')}
            </span>
          </label>
          <textarea
            id="payout-reject-reason"
            data-testid="payout-reject-reason"
            className={cn(
              'block h-24 w-full rounded-md border bg-pw-bg p-2 text-sm focus:outline-none focus:ring-2',
              reasonError
                ? 'border-rose-500 focus:border-rose-500 focus:ring-rose-500/20'
                : 'border-pw-border focus:border-pw-primary focus:ring-pw-primary/20',
            )}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (reasonError) setReasonError(null);
            }}
            maxLength={500}
            aria-invalid={reasonError ? 'true' : 'false'}
            placeholder={t('payouts.reject.reasonPlaceholder')}
          />
          {reasonError ? (
            <p className="mt-1 text-xs text-rose-600 dark:text-rose-300" role="alert">
              {reasonError}
            </p>
          ) : (
            <p className="mt-1 text-xs text-pw-text-muted">
              {t('payouts.reject.reasonHelp')}
            </p>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            reset();
            onOpenChange(false);
          }}
          disabled={submitting}
        >
          {t('payouts.reject.cancel')}
        </Button>
        <Button
          type="button"
          variant="destructive"
          data-testid={`payout-reject-confirm-btn-${payout.id}`}
          onClick={() => void handleReject()}
          disabled={submitting}
        >
          {submitting
            ? t('payouts.reject.submitting')
            : t('payouts.reject.confirm')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Summary cards + chart
// ---------------------------------------------------------------------------

function SummaryCards({
  pendingCents,
  approvedThisMonthCents,
  isLoading,
}: {
  pendingCents: number;
  approvedThisMonthCents: number;
  isLoading: boolean;
}): JSX.Element {
  const { t } = useTranslation('admin');
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div
        className="rounded-xl border border-pw-border bg-pw-bg p-4"
        data-testid="payouts-summary-pending"
      >
        <p className="text-xs font-medium uppercase tracking-wide text-pw-text-muted">
          {t('payouts.queue.summary.totalPending')}
        </p>
        <div className="mt-1 text-2xl font-semibold text-pw-text">
          {isLoading ? (
            <span className="inline-block h-7 w-32 animate-pulse rounded bg-pw-surface" />
          ) : (
            <Money usdCents={pendingCents} />
          )}
        </div>
      </div>
      <div
        className="rounded-xl border border-pw-border bg-pw-bg p-4"
        data-testid="payouts-summary-approved"
      >
        <p className="text-xs font-medium uppercase tracking-wide text-pw-text-muted">
          {t('payouts.queue.summary.approvedThisMonth')}
        </p>
        <div className="mt-1 text-2xl font-semibold text-pw-text">
          {isLoading ? (
            <span className="inline-block h-7 w-32 animate-pulse rounded bg-pw-surface" />
          ) : (
            <Money usdCents={approvedThisMonthCents} />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Lightweight status-distribution bar chart for the visible rows.
 *
 * The richer monthly-trend chart called out by belongs to
 * the Reports phase (08.3) — here we surface a per-status count so the
 * page still satisfies the "bar chart" acceptance bullet without pulling
 * a charting dep into the admin bundle.
 */
function StatusBarChart({ rows }: { rows: ReadonlyArray<PayoutRow> }): JSX.Element {
  const { t } = useTranslation('admin');
  const buckets = useMemo(() => {
    const counts: Record<PayoutStatus, number> = {
      Pending: 0,
      Processing: 0,
      Completed: 0,
      Failed: 0,
    };
    for (const r of rows) {
      counts[r.status] = (counts[r.status] ?? 0) + 1;
    }
    return counts;
  }, [rows]);

  const total = rows.length;
  const items: Array<{
    key: PayoutStatus;
    label: string;
    color: string;
    count: number;
  }> = [
    { key: 'Pending', label: t('payouts.queue.tab.pending'), color: 'bg-pw-primary', count: buckets.Pending },
    { key: 'Processing', label: t('payouts.queue.tab.processing'), color: 'bg-amber-500', count: buckets.Processing },
    { key: 'Completed', label: t('payouts.queue.tab.completed'), color: 'bg-emerald-500', count: buckets.Completed },
    { key: 'Failed', label: t('payouts.queue.tab.failed'), color: 'bg-rose-500', count: buckets.Failed },
  ];

  return (
    <div
      className="rounded-xl border border-pw-border bg-pw-bg p-4"
      data-testid="payouts-summary-chart"
    >
      <div className="mb-3 flex items-baseline justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-pw-text-muted">
          {t('payouts.queue.chart.title')}
        </p>
        <p className="text-xs text-pw-text-muted">
          {total} {t('payouts.queue.chart.countSuffix')}
        </p>
      </div>
      {total === 0 ? (
        <p className="text-sm text-pw-text-muted">
          {t('payouts.queue.chart.noData')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((item) => {
            const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
            return (
              <li key={item.key} className="flex items-center gap-3 text-xs">
                <span className="w-24 shrink-0 text-pw-text-muted">
                  {item.label}
                </span>
                <div className="relative h-2 flex-1 overflow-hidden rounded bg-pw-surface">
                  <div
                    className={cn('absolute inset-y-0 left-0', item.color)}
                    style={{ width: `${pct}%` }}
                    aria-hidden="true"
                  />
                </div>
                <span className="w-16 shrink-0 text-right tabular-nums text-pw-text">
                  {item.count} ({pct}%)
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function PayoutsQueuePage(): JSX.Element {
  const qc = useQueryClient();
  const { t } = useTranslation('admin');
  const formatRelativeTime = useFormatRelativeTime();

  const [tab, setTab] = useState<TabKey>('pending');
  // Pre-KYC filter chip — orthogonal to the status tab.
  const [preKycOnly, setPreKycOnly] = useState(false);

  const [approveTarget, setApproveTarget] = useState<PayoutRow | null>(null);
  const [rejectTarget, setRejectTarget] = useState<PayoutRow | null>(null);
  const [markTransferredTarget, setMarkTransferredTarget] =
    useState<PayoutRow | null>(null);

  // Phase 8.2 audit 2026-05-23 — URL-synced page + pageSize per /stores standard.
  // Live values clamped to BE allowlist (1..100); default 25 matches the
  // PageListFooter pageSizeChoices [10, 25, 50, 100].
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number.parseInt(searchParams.get('page') ?? '1', 10) || 1);
  const rawPageSize = Number.parseInt(searchParams.get('pageSize') ?? '25', 10);
  const pageSize = Number.isFinite(rawPageSize) && rawPageSize > 0
    ? Math.min(100, Math.max(10, rawPageSize))
    : 25;

  const setPage = (nextPage: number): void => {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setSearchParams(next, { replace: true });
  };
  const handlePageSizeChange = (size: number): void => {
    const next = new URLSearchParams(searchParams);
    next.set('pageSize', String(size));
    next.delete('page'); // reset to page 1 so user never lands on stale tail
    setSearchParams(next, { replace: true });
  };

  const filters = useMemo<PayoutListFilters>(() => {
    const apiStatus = TABS.find((d) => d.key === tab)?.apiStatus;
    const next: PayoutListFilters = {
      page,
      limit: pageSize,
      sort: '-created_at',
    };
    if (apiStatus) next.status = apiStatus;
    return next;
  }, [tab, page, pageSize]);

  const payoutsQuery = useQuery({
    queryKey: financeKeys.payouts.list(filters),
    queryFn: () => listPayouts(filters),
    staleTime: 30_000,
  });

  const allRows = useMemo(
    () => payoutsQuery.data?.rows ?? [],
    [payoutsQuery.data?.rows],
  );
  const visibleRows = useMemo(() => {
    if (!preKycOnly) return allRows;
    return allRows.filter((r) => r.kyc_status !== 'approved');
  }, [allRows, preKycOnly]);

  const summary = payoutsQuery.data?.meta.summary ?? {
    total_pending_cents: 0,
    approved_this_month_cents: 0,
  };
  const total = payoutsQuery.data?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: financeKeys.payouts.all });
  };

  return (
    <main
      className="flex flex-col gap-6 p-6"
      data-testid="admin-payouts-page"
    >
      <PageHeader title={t('payouts.queue.page.title')} subtitle={t('payouts.queue.page.subtitle')} />

      <SummaryCards
        pendingCents={summary.total_pending_cents}
        approvedThisMonthCents={summary.approved_this_month_cents}
        isLoading={payoutsQuery.isLoading}
      />

      <StatusBarChart rows={visibleRows} />

      {/* Tabs + Pre-KYC chip — chip is orthogonal so a single click below
          stacks with the status tab (e.g. Pending + Pre-KYC pending). */}
      <div className="flex flex-wrap items-center gap-2">
        <div
          className="flex flex-wrap gap-2"
          role="tablist"
          aria-label={t('payouts.queue.filter.ariaLabel')}
        >
          {TABS.map((d) => {
            const isActive = tab === d.key;
            return (
              <button
                key={d.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => {
                  setTab(d.key);
                  setPage(1);
                }}
                className={cn(
                  'inline-flex h-10 items-center rounded-md border px-4 text-sm font-semibold transition-colors',
                  isActive ? d.activeClass : d.idleClass,
                )}
                data-testid={`payouts-tab-${d.key}`}
              >
                {t(d.labelKey)}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => setPreKycOnly((v) => !v)}
          aria-pressed={preKycOnly}
          className={cn(
            'inline-flex h-10 items-center rounded-full border px-4 text-xs font-semibold transition-colors',
            preKycOnly
              ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300'
              : 'border-pw-border bg-pw-bg text-pw-text-muted hover:bg-pw-surface',
          )}
          data-testid="payouts-filter-prekyc-chip"
        >
          {t('payouts.queue.filter.preKycChip')} {preKycOnly ? '✓' : ''}
        </button>

        <span className="ml-auto text-xs text-pw-text-muted">
          {payoutsQuery.isFetching
            ? t('payouts.queue.filter.loading')
            : t('payouts.queue.filter.results', { count: visibleRows.length })}
        </span>
      </div>

      {/* List card — NO bulk checkbox column */}
      <div className="overflow-hidden rounded-xl border border-pw-border bg-pw-bg">
        <div className="grid grid-cols-[2fr_1fr_1fr_1.2fr_1fr_auto] gap-4 border-b border-pw-border bg-orange-50 dark:bg-orange-950/40 px-6 py-3 text-xs font-medium uppercase tracking-wider text-pw-text-muted">
          <div>{t('payouts.queue.column.vendor')}</div>
          <div>{t('payouts.queue.column.net')}</div>
          <div>{t('payouts.queue.column.gateway')}</div>
          <div>{t('payouts.queue.column.status')}</div>
          <div>{t('payouts.queue.column.createdAt')}</div>
          <div className="text-right">{t('payouts.queue.column.action')}</div>
        </div>

        {payoutsQuery.isLoading ? (
          <div className="flex h-32 items-center justify-center text-sm text-pw-text-muted">
            {t('payouts.queue.list.loading')}
          </div>
        ) : payoutsQuery.isError ? (
          <div className="flex h-32 flex-col items-center justify-center gap-2 text-sm text-rose-600 dark:text-rose-300">
            <span>{t('payouts.queue.list.errorTitle')}</span>
            <Button size="sm" variant="secondary" onClick={refresh}>
              {t('payouts.queue.list.retry')}
            </Button>
          </div>
        ) : visibleRows.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-sm text-pw-text-muted">
            {preKycOnly
              ? t('payouts.queue.list.emptyPreKyc')
              : t('payouts.queue.list.emptyDefault')}
          </div>
        ) : (
          <ul className="divide-y divide-pw-border">
            {visibleRows.map((row) => {
              const isPending = row.status === 'Pending';
              const isProcessing = row.status === 'Processing';
              const isFailed = row.status === 'Failed';
              const preKycBlocked = row.kyc_status !== 'approved';
              return (
                <li
                  key={row.id}
                  className="grid grid-cols-[2fr_1fr_1fr_1.2fr_1fr_auto] items-start gap-4 px-6 py-4 hover:bg-pw-surface/50"
                  data-testid={`payout-row-${row.id}`}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-pw-text">
                      {row.vendor_name}
                    </p>
                    <p className="truncate text-[11px] text-pw-text-muted">
                      {t('payouts.queue.row.vendorPrefix', { vendorId: row.vendor_id })}
                    </p>
                    {preKycBlocked ? (
                      <span
                        className="mt-1 inline-flex items-center rounded-full border border-amber-300 bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:text-amber-300"
                        title={t('payouts.queue.row.preKycTitle')}
                        data-testid={`payout-prekyc-badge-${row.id}`}
                      >
                        {t('payouts.queue.row.preKycBadge', { kycStatus: row.kyc_status })}
                      </span>
                    ) : null}
                  </div>
                  <div className="text-sm font-semibold text-pw-text">
                    <Money usdCents={row.net_cents} align="left" />
                    <p className="mt-0.5 text-[10px] text-pw-text-muted">
                      gross {(row.amount_cents / 100).toFixed(2)} − fee{' '}
                      {(row.fee_cents / 100).toFixed(2)}
                    </p>
                  </div>
                  <div className="text-sm text-pw-text">{row.gateway_code}</div>
                  <div>
                    <BillingStatusBadge status={row.status} />
                    {isFailed ? (
                      <p
                        className="mt-1 text-[11px] text-rose-600 dark:text-rose-300"
                        data-testid={`payout-failed-no-retry-notice-${row.id}`}
                      >
                        {t('payouts.queue.row.failedNoRetry')}
                        {row.failure_reason ? (
                          <span className="block text-pw-text-muted">
                            {t('payouts.queue.row.failedReason', { reason: row.failure_reason })}
                          </span>
                        ) : null}
                      </p>
                    ) : null}
                  </div>
                  <div className="text-sm text-pw-text-muted">
                    {formatRelativeTime(row.created_at)}
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    {isPending ? (
                      <>
                        <Button
                          size="sm"
                          data-testid={`payout-approve-btn-${row.id}`}
                          onClick={() => setApproveTarget(row)}
                          className="bg-emerald-500 text-white hover:bg-emerald-600"
                        >
                          {t('payouts.queue.row.approve')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`payout-reject-btn-${row.id}`}
                          onClick={() => setRejectTarget(row)}
                          className="border-rose-300 text-rose-600 dark:text-rose-300 hover:bg-rose-50"
                        >
                          {t('payouts.queue.row.reject')}
                        </Button>
                      </>
                    ) : isProcessing ? (
                      <Button
                        size="sm"
                        data-testid={`payout-mark-transferred-btn-${row.id}`}
                        onClick={() => setMarkTransferredTarget(row)}
                      >
                        {t('payouts.queue.row.markTransferred')}
                      </Button>
                    ) : (
                      <span className="text-[11px] text-pw-text-muted">
                        {t('payouts.queue.row.closed')}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {/* Pagination — canonical /stores footer (Rows-per-page + Total +
            numbered) per audit 2026-05-23 Cluster A. */}
        {total > 0 ? (
          <div className="border-t border-pw-border px-6 py-3">
            <PageListFooter
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              loading={payoutsQuery.isFetching}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              testId="payouts-footer"
            />
          </div>
        ) : null}
      </div>

      <ApproveConfirmDialog
        payout={approveTarget}
        open={!!approveTarget}
        onOpenChange={(next) => {
          if (!next) setApproveTarget(null);
        }}
        onConfirmed={refresh}
      />
      <RejectConfirmDialog
        payout={rejectTarget}
        open={!!rejectTarget}
        onOpenChange={(next) => {
          if (!next) setRejectTarget(null);
        }}
        onConfirmed={refresh}
      />
      <MarkTransferredModal
        open={!!markTransferredTarget}
        onOpenChange={(next) => {
          if (!next) setMarkTransferredTarget(null);
        }}
        payout={
          markTransferredTarget
            ? ({
                id: markTransferredTarget.id,
                vendor_name: markTransferredTarget.vendor_name,
                net_cents: markTransferredTarget.net_cents,
              } satisfies MarkTransferredPayoutSummary)
            : null
        }
        onSubmitted={refresh}
      />
    </main>
  );
}

export default PayoutsQueuePage;
